import type {DefinitionOrReference} from '@github-ui/code-nav'
import {Link} from '@github-ui/react-core/link'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useCodeHTML} from '@github-ui/syntax-highlighted-lines/hooks/use-code-html'
import {Link as PrimerLink} from '@primer/react'
import {clsx} from 'clsx'
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {measureMonospaceTextWidth} from '../../../../utilities/measure-text'
import {isHiddenUnicodeShown} from '../../Banners/HiddenUnicode'
import {HighlightedOverlay} from '../HighlightedOverlay'
import styles from './CodeNavCell.module.css'

function CodeNavCellInner({
  reference,
  isHighlighted,
  href,
  onClick,
  role,
  ariaLevel,
  symbol,
  index,
  focusElement,
}: {
  reference: DefinitionOrReference
  isHighlighted: boolean
  href: string
  onClick?: () => void
  role?: string
  ariaLevel?: number
  symbol: string
  index?: number
  focusElement?: boolean
}) {
  const [offsetWidth, setOffsetWidth] = useState<number | null>(null)
  const text = useMemo(() => getOffsetString(symbol, reference), [symbol, reference])

  // Canvas-based measurement: compute in a layout effect before paint
  useLayoutEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setOffsetWidth(measureMonospaceTextWidth(text))
  }, [text])

  useEffect(() => {
    if (focusElement) {
      const element = document.getElementById(`find-in-file-item-${index}`)
      if (element) {
        element.focus()
      }
    }
  }, [focusElement, index])

  const {tabSize} = useCurrentBlob()
  const hiddenUnicodeShown = isHiddenUnicodeShown()
  const codeHtml = useCodeHTML(
    reference.highlightedText,
    reference.stylingDirectives,
    reference.bodyText,
    'plain',
    tabSize,
    hiddenUnicodeShown,
  )

  const onClickHandler = useCallback(
    (ev: React.MouseEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!ev.ctrlKey && !ev.metaKey) {
        onClick?.()
      }
    },
    [onClick],
  )

  return (
    <PrimerLink
      as={Link}
      to={href}
      role={role}
      className={styles.cellLink}
      onClick={onClickHandler}
      onSelect={onClick}
      id={`find-in-file-item-${index}`}
      aria-current={isHighlighted ? 'location' : undefined}
      aria-level={ariaLevel}
      onKeyDown={e => {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.key === 'ArrowDown') {
          focusSibling('nextElementSibling')
          e.preventDefault()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (e.key === 'ArrowUp') {
          focusSibling('previousElementSibling')
          e.preventDefault()
        }
      }}
    >
      <div className={clsx(styles.cellInner, isHighlighted && styles.cellInnerHighlighted)}>
        <div className="d-flex">
          <div className={clsx('text-small blob-num color-fg-muted', styles.blobNum)}>{reference.lineNumber}</div>
          <div className={styles.codeContainer}>
            {offsetWidth !== null && (
              <div id={`offset-${reference.href(false)}`} style={{marginLeft: -offsetWidth}}>
                {symbol.length > 0 && (
                  <HighlightedOverlay
                    symbols={[reference]}
                    lineNumber={reference.lineNumber}
                    style={{overflow: 'initial'}}
                    isNotUsingWhitespace
                  />
                )}
                <VerifiedHTMLText
                  className={styles.verifiedHtml}
                  html={codeHtml}
                  aria-current={isHighlighted ? 'location' : undefined}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PrimerLink>
  )
}

const TOTAL_ALLOWED_CHARS = 34
export function getOffsetString(searchTerm: string, reference: DefinitionOrReference) {
  let canFitLength = TOTAL_ALLOWED_CHARS - searchTerm.length
  const substringBeforeMatch = reference.bodyText.slice(0, reference.ident.start.column)
  if (canFitLength <= 0) {
    // Fully offset to fit the match
    return substringBeforeMatch
  }

  // Maximize spacing based on the string after match
  const substringAfterMatch = reference.bodyText.slice(reference.ident.start.column + searchTerm.length).trimEnd()
  canFitLength = Math.max(canFitLength / 2, canFitLength - substringAfterMatch.length)
  const wordsOffset = substringBeforeMatch.split(' ')
  const fit: string[] = []

  for (let index = wordsOffset.length - 1; index >= 0; index--) {
    const word = wordsOffset[index]!
    fit.unshift(word)

    if (fit.join(' ').length <= canFitLength) {
      wordsOffset.pop()
    } else {
      break
    }
  }

  const offset = wordsOffset.join(' ')
  // If fitting strings starts from the whitespace(s) or tabs, add these characters to the offset as well.
  const whitespaceOffset = (substringBeforeMatch.slice(offset.length).match(/^[ \t]*/) || [])[0] ?? ''

  return `${wordsOffset.join(' ')}${whitespaceOffset}`
}

export function focusSibling(key: 'nextElementSibling' | 'previousElementSibling') {
  const {activeElement} = document
  const elementSibling = activeElement?.[key] as HTMLInputElement | null
  if (elementSibling) {
    if (elementSibling.role !== 'treeitem' && key === 'nextElementSibling') {
      // There is a group element that we don't want to focus between
      // the first level (expandable) and the second level
      const treeItem = elementSibling.querySelector<HTMLElement>('[role="treeitem"]')
      treeItem?.focus()
    } else {
      elementSibling.focus()
    }
  }
}

export const CodeNavCell = React.memo(CodeNavCellInner)
